<template>
  <div
    class="glass-frosted rounded-8xl p-4 flex flex-1 gap-4 max-w-136 w-70 min-w-fit whitespace-nowrap box-border"
    :class="{ 'disabled-all': !canEditColor }"
  >
    <SvPicker v-model:rgb="rgb" @change="changeColor" />
    <div class="flex-1 flex flex-col justify-end">
      <HuePicker v-model:rgb="rgb" @change="changeColor" />
      <RgbInput v-model="rgb" :disabled="!canEditColor" @change="changeColor" />
      <CurrentInput
        v-model="rgb"
        v-model:group-index="rgbGroupIndex"
        :rgb-group="rgbGroup"
        :has-liner-gradient="canEditGradient"
        :gradient-direction="gradientDirection"
        :is-colorful="isColorful"
        :disabled="!canEditColor"
        @change="changeColor"
      />
      <PreColorRadio
        v-model="rgb"
        :has-colorful="canEditColorCycle"
        :is-colorful="isColorful"
        :disabled="!canEditColor"
        @change="changeColor"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'

  import {
    hasLightingEditableField,
    LIGHTING_EDITABLE_FIELD,
    type lightingColor,
    type LightingEffectDefinition,
    type RGB,
    type RGBGroup,
  } from '@rapoo/device-sdk'

  import SvPicker from '@/components/colorPicker/svPicker.vue'
  import HuePicker from '@/components/colorPicker/huePicker.vue'
  import RgbInput from '@/components/colorPicker/rgbInput.vue'
  import CurrentInput from '@/components/colorPicker/currentInput.vue'
  import PreColorRadio from '@/components/colorPicker/preColorRadio.vue'

  const props = withDefaults(
    defineProps<{
      modelValue?: RGB
      rgbGroup?: RGBGroup
      isColorful?: boolean
      gradientDirection?: number
      /** 当前编辑区灯效；由页面 props 下传，不再 inject */
      currentEffectInfo?: Pick<LightingEffectDefinition, 'editableFields'> | null
    }>(),
    {
      modelValue: () => ({ r: 0, g: 0, b: 0 }),
      currentEffectInfo: null,
    },
  )
  const emit = defineEmits(['update:modelValue', 'change', 'update:rgbGroup'])

  const rgbGroupIndex = ref(0)

  const canEditColor = computed(() =>
    hasLightingEditableField(props.currentEffectInfo, LIGHTING_EDITABLE_FIELD.Color),
  )
  const canEditColorCycle = computed(() =>
    hasLightingEditableField(props.currentEffectInfo, LIGHTING_EDITABLE_FIELD.ColorCycle),
  )
  const canEditGradient = computed(() =>
    hasLightingEditableField(props.currentEffectInfo, LIGHTING_EDITABLE_FIELD.Gradient),
  )

  const rgb = computed({
    get() {
      if (canEditGradient.value) {
        return props.rgbGroup?.[rgbGroupIndex.value] as RGB
      } else {
        return props.modelValue
      }
    },
    set(val) {
      if (canEditGradient.value) {
        const rgbGroup = JSON.parse(JSON.stringify(props.rgbGroup)) as RGBGroup
        if (rgbGroup && rgbGroupIndex.value < rgbGroup.length) {
          rgbGroup[rgbGroupIndex.value] = val
          emit('update:rgbGroup', rgbGroup)
          emit('update:modelValue', val)
        }
      } else {
        emit('update:modelValue', val)
      }
    },
  })

  const changeColor = (val: lightingColor) => {
    emit('change', val)
  }
</script>
